import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import moment from 'moment';
import { of, Subscription, switchMap } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';

import { SnackbarSaveDraftGuaranteeComponent } from '../../utils/snackbar-save-draft-guarantee.component';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { StepStatus, StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { User } from '../../../../shared/models/user';
import { ErrorService } from '../../../../shared/services/error.service';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { Guarantee } from '../../../models/guarantee';
import { GuaranteeStatus } from '../../../models/guarantee-constants';
import { GuaranteeRequestService } from '../../../services/guarantee-request.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import {
	FeedbackErrorComponent
} from '../../../../shared/components/feedback-error/feedback-error.component';

interface ErrorObject {

	soldTo: boolean;
	name: boolean;
}
type ApplicationType =
	'Silo' | 'Road restraint systems' | 'Acoustic barriers' | 'Perforated sheet' | 'Other Applications';

@Component({
	selector: 'copo-project-characteristics-other',
	templateUrl: './project-characteristics-other.component.html',
	styleUrl: './project-characteristics-other.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		FormsModule,
		StepperComponent,

		MatFormFieldModule,

		MatInputModule,
		ReactiveFormsModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,

		MatRadioModule,

		MatCheckboxModule,
		LoadingSpinnerComponent,
		FeedbackErrorComponent,
	]
})
export class ProjectCharacteristicsOtherComponent implements OnInit, OnDestroy {
	@ViewChild('soldToDiv', { read: ElementRef }) soldToDiv!: ElementRef<HTMLElement>;
	@ViewChild('nameDiv', { read: ElementRef }) nameDiv!: ElementRef<HTMLElement>;
	@Input() guaranteeResponse!: Guarantee;

	submitting = false;
	isLoading = true;
	soldTos: BocClient[] | undefined;
	guarantee!: Guarantee;
	user: User;
	readonly steps;

	onErrorMandatory = {
		soldTo: false,
		name: false,
	};

	soldToFormControl!: FormControl<string>;
	magnelisTonnageFormControl!: FormControl<string>;
	applicationTypeFormControl!: FormControl<ApplicationType | null>;
	siloBaseFormControl!: FormControl<boolean>;
	siloProductsStoredFormControl!: FormControl<string>;
	linerInSiloFormControl!: FormControl<boolean>;
	siloRoofFormControl!: FormControl<boolean>;
	siloCylinderFormControl!: FormControl<boolean>;
	siloInContactSoilConcreteFormControl!: FormControl<boolean>;
	rrsHorizontalFormControl!: FormControl<boolean>;
	rrsMiscellaneousFormControl!: FormControl<boolean>;
	rrsMagnelisWeldedFormControl!: FormControl<boolean>;
	rrsPolesFormControl!: FormControl<boolean>;
	acoRoadTrafficFormControl!: FormControl<boolean>;
	acoRailroadFormControl!: FormControl<boolean>;
	acoConcreteBaseFormControl!: FormControl<boolean>;
	peforatedSheetFormControl!: FormControl<boolean>;
	perAppFormControl!: FormControl<string>;
	perPerRichFormControl!: FormControl<string>;
	projectNameFormControl!: FormControl<string>;
	toaOtherApplicationFormControl!: FormControl<string>;
	private soldToSub!: Subscription;
	constructor(
		private readonly locationService: LocationService,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly guaranteeService: GuaranteeRequestService,

		private readonly errorService: ErrorService,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('guarantee.project.characteristics'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('guarantee.project.environment'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('guarantee.type'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('guarantee.summary'),
				status: StepStatus.DISABLED,
			}
		];
		this.user = this.userService.user;
	}

	ngOnInit() {
		this.errorService.resetErrorMsg();
		this.guarantee = this.guaranteeResponse;
		this.magnelisTonnageFormControl = new FormControl(this.guarantee.dataOnTheProjectSize, { nonNullable: true });
		this.applicationTypeFormControl = new FormControl(
			this.guarantee.toaSilo ? 'Silo'
				:	this.guarantee.toaRoadResSystem ? 'Road restraint systems'
					:	this.guarantee.toaAcousticBarrier ? 'Acoustic barriers'
						:	this.guarantee.toaPerforatedSheet ? 'Perforated sheet'
							:	this.guarantee.isTOAOtherApps ? 'Other Applications' : null
		);
		this.siloBaseFormControl = new FormControl(this.guarantee.siloBase, { nonNullable: true });
		this.linerInSiloFormControl = new FormControl(this.guarantee.linerInSilo, { nonNullable: true });
		this.siloProductsStoredFormControl = new FormControl(this.guarantee.siloProductsStored, { nonNullable: true });
		this.siloRoofFormControl = new FormControl(this.guarantee.siloRoof, { nonNullable: true });
		this.siloCylinderFormControl = new FormControl(this.guarantee.siloCylinder, { nonNullable: true });
		this.siloInContactSoilConcreteFormControl = new FormControl(
			this.guarantee.siloInContactSoilConcrete,
			{ nonNullable: true }
		);
		this.rrsHorizontalFormControl = new FormControl(this.guarantee.rrsHorizontal, { nonNullable: true });
		this.rrsMiscellaneousFormControl = new FormControl(this.guarantee.rrsMiscellaneous, { nonNullable: true });
		this.rrsMagnelisWeldedFormControl = new FormControl(this.guarantee.rrsMagnelisWelded, { nonNullable: true });
		this.rrsPolesFormControl = new FormControl(this.guarantee.rrsPoles, { nonNullable: true });
		this.acoRoadTrafficFormControl = new FormControl(this.guarantee.acoRoadTraffic, { nonNullable: true });
		this.acoRailroadFormControl = new FormControl(this.guarantee.acoRailroad, { nonNullable: true });
		this.toaOtherApplicationFormControl = new FormControl(
			this.guarantee.toaOtherApplication,
			{ nonNullable: true }
		);
		this.acoConcreteBaseFormControl = new FormControl(this.guarantee.acoConcreteBase, { nonNullable: true });
		this.peforatedSheetFormControl = new FormControl(this.guarantee.peforatedSheet, { nonNullable: true });
		this.perAppFormControl = new FormControl(this.guarantee.perApp, { nonNullable: true });
		this.perPerRichFormControl = new FormControl(this.guarantee.perPerRich, { nonNullable: true });
		this.soldToFormControl = new FormControl(
			this.guarantee.soldTo,
			{ validators: ctrl => this.onErrorMandatory.soldTo ? Validators.required(ctrl) : null, nonNullable: true }
		);
		this.projectNameFormControl = new FormControl(
			this.guarantee.name,
			{ validators: ctrl => this.onErrorMandatory.name ? Validators.required(ctrl) : null, nonNullable: true }
		);
		this.soldToSub = this.userService.getSoldTosTransac('PGNTE')
			.pipe(

				switchMap(businessIds => {
					if (businessIds.length === 0) {
						return of(undefined);
					}

					return this.userService.getBocs(
						['name', 'businessId'],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					);
				})
			).subscribe({
				next: soldToList => {
					this.soldTos = soldToList?.results;
					if (this.soldTos && this.soldTos.length !== 0 && !this.guarantee.soldTo) {
						this.guarantee.soldTo = this.soldTos[0].businessId;
						this.soldToFormControl.setValue(this.guarantee.soldTo);
					}
				},
				complete: () => {
					this.isLoading = false;
				}

			});
	}

	ngOnDestroy() {
		this.soldToSub.unsubscribe();
	}

	async saveGuarantee(event: Event) {
		event.preventDefault();
		this.resetOnError();
		if (!this.isNameValid(event) || !this.isSoldToValid(event)) {
			return;
		}
		const now = moment().toISOString();
		const userName = `${ this.user.firstname } ${ this.user.lastname }`;
		const userId = this.user.id;
		if (this.guarantee.status !== GuaranteeStatus.DRAFT) {
			this.guarantee.requestDate = now;
		}
		this.guarantee.requesterName = userName;
		this.guarantee.requesterId = userId;

		this.submitting = true;
		try {
			await this.guaranteeService.saveGuarantee(this.guarantee);
		} catch (err) {
			console.error(err);
			this.guarantee.status = GuaranteeStatus.DRAFT;
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: true
				}
			);
			return;
		} finally {
			this.submitting = false;
		}
		if (this.guarantee.status === GuaranteeStatus.DRAFT) {
			await this.router.navigate(['/guarantee-requests/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar']
				}
			);
		}
	}

	resetOnError() {
		this.onErrorMandatory.soldTo = false;
		this.onErrorMandatory.name = false;
	}

	onErrorForm(elem: ElementRef, onError: keyof ErrorObject, event: Event) {
		(<HTMLElement> elem.nativeElement).scrollIntoView();
		this.onErrorMandatory[onError] = true;
		event.preventDefault();
	}

	isSoldToValid(event: Event) {
		if (this.guarantee.soldTo === '') {
			this.onErrorForm(this.soldToDiv, 'soldTo', event);
			this.soldToFormControl.markAsTouched();
			this.soldToFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isNameValid(event: Event) {
		const trimmedName = this.guarantee.name.trim();
		if (trimmedName === '') {
			this.guarantee.name = '';
			this.projectNameFormControl.setValue('', { emitEvent: false });
			this.onErrorForm(this.nameDiv, 'name', event);
			this.projectNameFormControl.markAsTouched();
			this.projectNameFormControl.setErrors({ required: true });
			this.projectNameFormControl.updateValueAndValidity();
			return false;
		}
		this.guarantee.name = trimmedName;
		this.projectNameFormControl.setValue(trimmedName, { emitEvent: false });
		return true;
	}

	async isNameUnique(): Promise<boolean> {
		this.isLoading = true;
		try {
			return await this.guaranteeService.checkNameUniquenessOrFeedback(this.guarantee.name);
		} finally {
			this.isLoading = false;
		}
	}

	async goToNextStep(event: Event) {
		this.resetOnError();
		// check mandatory fields
		// we can't push if any mandatory are not filled

		if (!this.isNameValid(event) || !this.isSoldToValid(event) || await this.isNameUnique()) {
			return;
		}

		await this.router.navigate([
			this.guarantee.id !== undefined
				? `/guarantee-requests/edit/${ this.guarantee.id }/project-environment`
				: '/guarantee-requests/new/project-environment'
		], { replaceUrl: true });
	}

	goBack() {
		if (this.guarantee.id !== undefined) {
			this.locationService.go(-1);
		} else {
			this.locationService.go(-1, '../guarantee-requests/new/guarantee-type-selector');
		}
	}

	soldToChanged(event: MatSelectChange) {
		this.guarantee.soldTo = <string> event.value;
		this.errorService.resetErrorMsg();
	}

	magnelisTonnageNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.dataOnTheProjectSize = target.value;
	}

	applicationTypeChange(event: MatRadioChange | Event) {
		if (!(event instanceof MatRadioChange) || event.source.name !== 'application') {
			return;
		}
		this.guarantee.toaSilo = event.value === 'Silo';
		this.guarantee.toaRoadResSystem = event.value === 'Road restraint systems';
		this.guarantee.toaAcousticBarrier = event.value === 'Acoustic barriers';
		this.guarantee.toaPerforatedSheet = event.value === 'Perforated sheet';
		this.guarantee.isTOAOtherApps = event.value === 'Other Applications';
		this.resetType(<ApplicationType> event.value);
	}

	resetType(applicationType: ApplicationType) {
		if (applicationType !== 'Silo') {
			this.siloReset();
		}
		if (applicationType !== 'Road restraint systems') {
			this.roadRestraintSystemsReset();
		}
		if (applicationType !== 'Acoustic barriers') {
			this.acousticBarriersReset();
		}
		if (applicationType !== 'Perforated sheet') {
			this.perforatedSheetReset();
		}
		if (applicationType !== 'Other Applications') {
			this.otherApplcationsReset();
		}
	}

	onSiloBaseChange(checked: boolean) {
		this.guarantee.siloBase = checked;
	}

	onSiloProductsStoredChange(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.siloProductsStored = target.value;
	}

	onLinerInSiloChange(event: MatRadioChange) {
		this.guarantee.linerInSilo = Boolean(event.value);
	}

	onSiloRoofChange(checked: boolean) {
		this.guarantee.siloRoof = checked;
	}

	onSiloCylinderChange(checked: boolean) {
		this.guarantee.siloCylinder = checked;
	}

	onSiloInContactSoilConcreteChange(checked: boolean) {
		this.guarantee.siloInContactSoilConcrete = checked;
	}

	rrsHorizontalChanged(checked: boolean) {
		this.guarantee.rrsHorizontal = checked;
	}

	rrsMiscellaneousChanged(checked: boolean) {
		this.guarantee.rrsMiscellaneous = checked;
		if (!checked) {
			this.rrsMagnelisWeldedFormControl.reset(false);
			this.guarantee.rrsMagnelisWelded = false;
		}
	}

	rrsMagnelisWeldedChanged(event: MatRadioChange) {
		this.guarantee.rrsMagnelisWelded = Boolean(event.value);
	}

	rrsPolesChanged(checked: boolean) {
		this.guarantee.rrsPoles = checked;
	}

	acoRoadTrafficChanged(checked: boolean) {
		this.guarantee.acoRoadTraffic = checked;
	}

	acoRailroadChanged(checked: boolean) {
		this.guarantee.acoRailroad = checked;
	}

	acoConcreteBaseChanged(event: MatRadioChange) {
		this.guarantee.acoConcreteBase = Boolean(event.value);
	}

	peforatedSheetChanged(checked: boolean) {
		this.guarantee.peforatedSheet = checked;
	}

	perAppChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.perApp = target.value;
	}

	perPerRichChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.perPerRich = target.value;
	}

	projectNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.name = target.value.trim();
		this.projectNameFormControl.setValue(this.guarantee.name, { emitEvent: false });
		this.errorService.resetErrorMsg();
	}

	toaOtherApplicationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.toaOtherApplication = target.value;
	}

	siloReset() {
		this.siloBaseFormControl.reset(false);
		this.linerInSiloFormControl.reset(false);
		this.siloProductsStoredFormControl.reset('');
		this.siloRoofFormControl.reset(false);
		this.siloCylinderFormControl.reset(false);
		this.siloInContactSoilConcreteFormControl.reset(false);
		this.guarantee.toaSilo = false;
		this.guarantee.siloBase = false;
		this.guarantee.linerInSilo = false;
		this.guarantee.siloProductsStored = '';
		this.guarantee.siloRoof = false;
		this.guarantee.siloCylinder = false;
		this.guarantee.siloInContactSoilConcrete = false;
	}

	roadRestraintSystemsReset() {
		this.rrsHorizontalFormControl.reset(false);
		this.rrsMiscellaneousFormControl.reset(false);
		this.rrsMagnelisWeldedFormControl.reset(false);
		this.rrsPolesFormControl.reset(false);
		this.guarantee.toaRoadResSystem = false;
		this.guarantee.rrsHorizontal = false;
		this.guarantee.rrsMiscellaneous = false;
		this.guarantee.rrsPoles = false;
		this.guarantee.rrsMagnelisWelded = false;
	}

	acousticBarriersReset() {
		this.acoRoadTrafficFormControl.reset(false);
		this.acoRailroadFormControl.reset(false);
		this.acoConcreteBaseFormControl.reset(false);
		this.guarantee.toaAcousticBarrier = false;
		this.guarantee.acoRoadTraffic = false;
		this.guarantee.acoRailroad = false;
		this.guarantee.acoConcreteBase = false;
	}

	perforatedSheetReset() {
		this.peforatedSheetFormControl.reset(false);
		this.perPerRichFormControl.reset('');
		this.perAppFormControl.reset('');
		this.guarantee.toaPerforatedSheet = false;
		this.guarantee.peforatedSheet = false;
		this.guarantee.perPerRich = '';
		this.guarantee.perApp = '';
	}

	otherApplcationsReset() {
		this.toaOtherApplicationFormControl.reset('');
		this.guarantee.isTOAOtherApps = false;
		this.guarantee.toaOtherApplication = '';
	}
}
